/* Gotta collection filters v3.
 * Native filters provide candidates. gotta-stock-grid.js verifies one matching
 * purchasable variant, then computes the complete count and logical pages.
 * Non-strict views retain native rendering.
 */
(() => {
  'use strict';
  if (window.GottaFacetController) {
    window.GottaFacetController.bootstrap();
    return;
  }

  const AVAILABILITY = 'filter.v.availability';
  const TRANSIENT = new Set(['page', 'phcursor', 'section_id', 'sections']);
  const FORM_IDS = ['FacetFiltersForm', 'FacetFiltersFormMobile', 'FacetFiltersPillsForm', 'FacetSortForm', 'FacetSortDrawerForm'];
  const root = () => document.getElementById('ProductGridContainer');
  const notify = (name, detail = {}) => document.dispatchEvent(new CustomEvent(name, { detail }));

  class FacetFiltersForm extends HTMLElement {
    constructor() {
      super();
      // Delegation survives replacement of the form contents after an AJAX request.
      this.addEventListener('input', (event) => {
        if (!event.target.matches('input, select')) return;
        FacetFiltersForm.invalidate(); // Invalidate NOW, not after the debounce delay.
        const params = this.formSearchParams(event.target.closest('form'));
        FacetFiltersForm.timer = setTimeout(() => this.onSubmitForm(params, event), 250);
      });
      this.addEventListener('submit', (event) => {
        event.preventDefault();
        this.onSubmitForm(this.formSearchParams(event.target.closest('form')), event);
      });
      this.addEventListener('keyup', (event) => {
        if (event.target.closest('#FacetsWrapperDesktop') && typeof onKeyUpEscape === 'function') onKeyUpEscape(event);
      });
    }

    static autoAvailability() {
      return root()?.dataset.gottaNativeGrid === 'true' && root().dataset.autoAvailability === 'true';
    }

    static normalize(searchParams, resetPage = false) {
      const params = new URLSearchParams(searchParams);
      params.delete('section_id');
      params.delete('sections');
      if (resetPage) {
        params.delete('page');
        params.delete('phcursor');
      }
      if (FacetFiltersForm.autoAvailability()) params.set(AVAILABILITY, '1');
      const defaultSort = root()?.dataset.defaultSort || (root()?.dataset.stockContext === 'search' ? 'relevance' : '');
      if (defaultSort && defaultSort !== 'collection' && !params.get('sort_by')) params.set('sort_by', defaultSort);
      // Keep multiple values for a filter, but remove identical hidden inputs from multiple forms.
      const unique = new URLSearchParams();
      const seen = new Set();
      for (const [key, value] of params) {
        const pair = JSON.stringify([key, value]);
        if (seen.has(pair)) continue;
        seen.add(pair);
        unique.append(key, value);
      }
      return unique.toString();
    }

    static invalidate() {
      clearTimeout(FacetFiltersForm.timer);
      FacetFiltersForm.sequence += 1;
      FacetFiltersForm.controller?.abort();
      FacetFiltersForm.controller = null;
      FacetFiltersForm.setBusy(true);
      notify('gotta:facets:loading'); // Also cancels a pending infinite-scroll page immediately.
    }

    static setBusy(busy) {
      const live = root();
      if (!live) return;
      live.setAttribute('aria-busy', String(busy));
      live.querySelector('.collection')?.classList.toggle('loading', busy);
      ['ProductCount', 'ProductCountDesktop', 'SearchResultCount'].forEach((id) => document.getElementById(id)?.classList.toggle('loading', busy));
      document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
        .forEach((spinner) => spinner.classList.toggle('hidden', !busy));
    }

    static async renderPage(searchParams, event = null, updateURLHash = true, options = {}) {
      const live = root();
      const section = document.getElementById('product-grid')?.dataset.id || live?.dataset.sectionId;
      if (!live || !section) return false;
      FacetFiltersForm.invalidate();
      const id = FacetFiltersForm.sequence;
      let normalized = FacetFiltersForm.normalize(searchParams, Boolean(options.resetPage));
      const controller = new AbortController();
      FacetFiltersForm.controller = controller;
      FacetFiltersForm.lastAttempt = normalized;
      FacetFiltersForm.setBusy(true);
      live.querySelector('.gotta-facets-error')?.setAttribute('hidden', '');
      const requestURL = new URL(window.location.href);
      requestURL.search = normalized;
      requestURL.searchParams.set('section_id', section);
      const strictStock = live.dataset.stockVersion === '3' && FacetFiltersForm.autoAvailability();
      if (strictStock) {
        requestURL.searchParams.delete('page'); // Native page 1 provides filters/settings, not the logical result page.
        requestURL.searchParams.delete('phcursor');
      }
      requestURL.hash = '';
      let timedOut = false;
      const timeout = setTimeout(() => { timedOut = true; controller.abort(); }, 45000);
      try {
        const indexTask = strictStock && window.GottaStockGrid?.prefetchIndex
          ? window.GottaStockGrid.prefetchIndex(normalized, controller.signal, live)
            .then((index) => ({ index }), (error) => ({ error }))
          : null;
        let parsed = options.initialDocument;
        if (!parsed) {
          const response = await fetch(requestURL.toString(), {
            signal: controller.signal,
            credentials: 'same-origin', cache: 'no-store',
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
          });
          if (!response.ok) throw new Error(`Section request failed (${response.status})`);
          parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        }
        if (id !== FacetFiltersForm.sequence || !live.isConnected || live !== root()) return false;
        const source = parsed.getElementById('ProductGridContainer');
        const sourceGrid = parsed.getElementById('product-grid');
        if (!source || !sourceGrid || sourceGrid.dataset.id !== section) throw new Error('Invalid section response');
        if (strictStock) {
          if (!window.GottaStockGrid) throw new Error('Missing v3 availability engine');
          const result = await window.GottaStockGrid.prepare(parsed, normalized, { section, signal: controller.signal, indexTask });
          if (id !== FacetFiltersForm.sequence || !live.isConnected || live !== root()) return false;
          parsed = result.parsed;
          normalized = result.searchParams;
          window.GottaStockGrid.lastReport = result.report;
        } else if (FacetFiltersForm.autoAvailability() && Number(source.dataset.productCount) > 0 &&
            (source.dataset.availabilityFilterEnabled !== 'true' || source.dataset.availabilityApplied !== 'true')) {
          throw new Error('Native availability filter was not applied. Check Search & Discovery.');
        }
        FacetFiltersForm.renderFilters(parsed);
        FacetFiltersForm.renderProductGridContainer(parsed);
        FacetFiltersForm.renderProductCount(parsed);
        live.classList.remove('gotta-facets-failed');
        const region = live.querySelector('.product-grid-container') || live.querySelector('#product-grid');
        if (region) region.hidden = false;
        FacetFiltersForm.searchParamsPrev = normalized;
        if (updateURLHash || options.replaceURL) FacetFiltersForm.updateURLHash(normalized, Boolean(options.replaceURL));
        FacetFiltersForm.setBusy(false);
        FacetFiltersForm.refreshCards(live);
        notify('gotta:facets:rendered', { searchParams: normalized, section });
        return true;
      } catch (error) {
        if (id !== FacetFiltersForm.sequence || !live.isConnected || live !== root()) return false;
        if (error.name === 'AbortError' && !timedOut) return false;
        console.warn('[Gotta filters]', error.message);
        FacetFiltersForm.showError(normalized);
        return false;
      } finally {
        clearTimeout(timeout);
        controller.abort();
        if (id === FacetFiltersForm.sequence) {
          FacetFiltersForm.setBusy(false);
          FacetFiltersForm.controller = null;
        }
      }
    }

    static showError(searchParams) {
      const live = root();
      if (!live) return;
      live.classList.add('gotta-facets-failed');
      const region = live.querySelector('.product-grid-container') || live.querySelector('#product-grid');
      if (region) region.hidden = true; // Never label old products as results of the new selection.
      let message = live.querySelector('.gotta-facets-error');
      if (!message) {
        message = document.createElement('div');
        message.className = 'gotta-facets-error';
        message.setAttribute('role', 'alert');
        const text = document.createElement('p');
        text.textContent = 'No se pudieron actualizar los productos. Reintenta para ver los resultados de tu selección.';
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'button button--secondary';
        button.textContent = 'Reintentar';
        button.addEventListener('click', () => FacetFiltersForm.renderPage(FacetFiltersForm.lastAttempt));
        message.append(text, button);
        live.prepend(message);
      }
      FacetFiltersForm.lastAttempt = searchParams;
      message.hidden = false;
      ['ProductCount', 'ProductCountDesktop', 'SearchResultCount'].forEach((id) => {
        const label = document.getElementById(id);
        if (label) label.textContent = '—';
      });
      document.querySelectorAll('.mobile-facets__count').forEach((node) => { node.textContent = '—'; });
      notify('gotta:facets:error');
    }

    static renderFilters(parsed) {
      const active = document.activeElement;
      const activeId = active?.id;
      const selection = active && typeof active.selectionStart === 'number' ? [active.selectionStart, active.selectionEnd] : null;
      const details = new Map();
      const expanded = new Set();
      document.querySelectorAll('facet-filters-form details[id]').forEach((item) => {
        details.set(item.id, { open: item.open, opening: item.classList.contains('menu-opening') });
        if (item.querySelector('.label-show-more.hidden')) expanded.add(item.id);
      });
      const mobileDrawer = document.getElementById('FacetFiltersFormMobile')?.closest('menu-drawer');
      const mobileWasOpen = mobileDrawer?.querySelector('details')?.open;
      const scrollStates = new Map();
      document.querySelectorAll('facet-filters-form [id]').forEach((item) => {
        if (item.scrollTop) scrollStates.set(item.id, item.scrollTop);
      });
      FORM_IDS.forEach((formId) => {
        const target = document.getElementById(formId);
        const source = parsed.getElementById(formId);
        if (target && source) target.innerHTML = source.innerHTML;
      });
      // The mobile pills are outside the forms.
      ['.active-facets-mobile', '.mobile-facets__open'].forEach((selector) => {
        const target = document.querySelector(selector);
        const source = parsed.querySelector(selector);
        if (target && source) target.innerHTML = source.innerHTML;
      });
      details.forEach((state, key) => {
        const item = document.getElementById(key);
        if (!item) return;
        item.open = state.open;
        item.classList.toggle('menu-opening', state.open && state.opening);
        item.querySelector('summary')?.setAttribute('aria-expanded', String(state.open));
        if (state.open && state.opening) item.closest('.has-submenu')?.classList.add('submenu-open');
        if (expanded.has(key)) {
          item.querySelectorAll('.show-more-item').forEach((node) => node.classList.remove('hidden'));
          item.querySelector('.label-show-more')?.classList.add('hidden');
          item.querySelector('.label-show-less')?.classList.remove('hidden');
        }
      });
      // Bind only newly-created mobile descendants, not the unchanged outer summary.
      const mobileForm = document.getElementById('FacetFiltersFormMobile');
      const drawer = mobileForm?.closest('menu-drawer');
      if (drawer && typeof drawer.onSummaryClick === 'function') {
        mobileForm.querySelectorAll('summary').forEach((summary) => summary.addEventListener('click', drawer.onSummaryClick.bind(drawer)));
        mobileForm.querySelectorAll('button:not(.localization-selector)').forEach((button) => {
          if (typeof drawer.onCloseButtonClick === 'function') button.addEventListener('click', drawer.onCloseButtonClick.bind(drawer));
        });
      }
      scrollStates.forEach((top, id) => { const node = document.getElementById(id); if (node) node.scrollTop = top; });
      const replacement = activeId ? document.getElementById(activeId) : null;
      if (replacement && (!mobileForm?.contains(replacement) || mobileWasOpen)) {
        replacement.focus({ preventScroll: true });
        if (selection && typeof replacement.setSelectionRange === 'function') {
          try { replacement.setSelectionRange(...selection); } catch (_) { /* Number inputs do not support text ranges. */ }
        }
      }
      // Refresh the focus trap after replacing a still-open mobile drawer.
      if (mobileWasOpen && drawer && typeof trapFocus === 'function') {
        const opened = drawer.querySelector('.mobile-facets__details[open] .mobile-facets__submenu') || drawer.querySelector('.mobile-facets__inner');
        if (opened) trapFocus(opened, replacement && opened.contains(replacement) ? replacement : opened.querySelector('button'));
      }
    }

    static renderProductGridContainer(parsed) {
      const source = parsed.getElementById('ProductGridContainer');
      const live = root();
      if (!source || !live) return;
      for (const [key, value] of Object.entries(source.dataset)) live.dataset[key] = value;
      const sourceRegion = source.querySelector('.product-grid-container');
      const liveRegion = live.querySelector('.product-grid-container');
      if (sourceRegion && liveRegion) liveRegion.innerHTML = sourceRegion.innerHTML;
      else live.innerHTML = source.innerHTML; // Search has the product region on the container itself.
      live.querySelectorAll('.scroll-trigger').forEach((node) => node.classList.add('scroll-trigger--cancel'));
    }

    static renderProductCount(parsed) {
      ['ProductCount', 'ProductCountDesktop', 'SearchResultCount'].forEach((id) => {
        const target = document.getElementById(id);
        const source = parsed.getElementById(id) || parsed.getElementById('ProductCount') || parsed.getElementById('ProductCountDesktop');
        if (target && source) target.innerHTML = source.innerHTML;
      });
      const mobileCount = document.querySelector('.mobile-facets__count');
      const sourceCount = parsed.querySelector('.mobile-facets__count');
      if (mobileCount && sourceCount) mobileCount.innerHTML = sourceCount.innerHTML;
    }

    static refreshCards(container) {
      try { if (typeof normalInfo === 'function') normalInfo(); } catch (error) { console.warn('[Gotta delivery]', error); }
      try { if (typeof window.refreshColorSwatches === 'function') window.refreshColorSwatches(); } catch (error) { console.warn('[Gotta swatches]', error); }
      try { if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(container); } catch (error) { console.warn('[Gotta animations]', error); }
    }

    static updateURLHash(searchParams, replace = false) {
      const next = `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}${window.location.hash}`;
      if (next === `${window.location.pathname}${window.location.search}${window.location.hash}`) return;
      // Preserve theme preview / other integrations' history state and the URL hash.
      const state = { ...(history.state || {}), searchParams };
      history[replace ? 'replaceState' : 'pushState'](state, '', next);
    }

    static getSections() {
      const section = document.getElementById('product-grid')?.dataset.id;
      return section ? [{ section }] : [];
    }

    formSearchParams(form) {
      if (!form) return FacetFiltersForm.normalize(window.location.search, true);
      const mobile = form.id === 'FacetFiltersFormMobile';
      const forms = mobile ? [form] : ['FacetFiltersForm', 'FacetSortForm', 'FacetSortDrawerForm']
        .map((id) => document.getElementById(id)).filter(Boolean);
      if (!forms.length) forms.push(form);
      // Retain non-filter context (UTMs, preview_theme_id, search q, locale, etc.).
      const params = new URLSearchParams();
      for (const [key, value] of new URLSearchParams(window.location.search)) {
        if (!key.startsWith('filter.') && !TRANSIENT.has(key)) params.append(key, value);
      }
      const submitted = new URLSearchParams();
      forms.forEach((item) => {
        for (const [key, value] of new FormData(item)) {
          if (typeof value === 'string' && !TRANSIENT.has(key)) submitted.append(key, value);
        }
      });
      new Set(submitted.keys()).forEach((key) => params.delete(key));
      submitted.forEach((value, key) => params.append(key, value));
      return FacetFiltersForm.normalize(params.toString(), true);
    }

    createSearchParams(form) { return new URLSearchParams(new FormData(form)).toString(); }
    onSubmitForm(searchParams, event) { return FacetFiltersForm.renderPage(searchParams, event, true, { resetPage: true }); }
    onSubmitHandler(event) { event.preventDefault(); return this.onSubmitForm(this.formSearchParams(event.target.closest('form')), event); }
    onActiveFilterClick(event) {
      event.preventDefault();
      const anchor = event.currentTarget instanceof HTMLAnchorElement ? event.currentTarget : event.target.closest('a');
      if (!anchor) return;
      const next = new URL(anchor.href, window.location.href);
      const params = next.searchParams;
      // A clear-all link does not carry preview/UTM context; keep it explicitly.
      for (const [key, value] of new URLSearchParams(window.location.search)) {
        if (!key.startsWith('filter.') && !TRANSIENT.has(key) && !params.has(key)) params.append(key, value);
      }
      return FacetFiltersForm.renderPage(params.toString(), event, true, { resetPage: true });
    }

    static bootstrap() {
      const live = root();
      if (!live || FacetFiltersForm.booted.has(live)) return;
      FacetFiltersForm.booted.add(live);
      FacetFiltersForm.searchParamsPrev = new URLSearchParams(window.location.search).toString();
      if (live.dataset.gottaNativeGrid !== 'true') return;
      const normalized = FacetFiltersForm.normalize(window.location.search);
      const needsAvailability = FacetFiltersForm.autoAvailability() && live.dataset.availabilityApplied !== 'true';
      const needsVariantCheck = live.dataset.stockVersion === '3' && FacetFiltersForm.autoAvailability();
      if (normalized !== new URLSearchParams(window.location.search).toString() || needsAvailability || needsVariantCheck) {
        const canReuseInitial = needsVariantCheck && !needsAvailability &&
          live.dataset.currentPage === '1' && live.dataset.stockVerified !== 'true';
        const initialDocument = canReuseInitial ? document.cloneNode(true) : null;
        FacetFiltersForm.renderPage(normalized, null, false, { replaceURL: true, initialDocument });
      } else notify('gotta:facets:ready');
    }
  }

  FacetFiltersForm.sequence = 0;
  FacetFiltersForm.controller = null;
  FacetFiltersForm.timer = null;
  FacetFiltersForm.booted = new WeakSet();
  FacetFiltersForm.filterData = []; // Retained only for integrations; never used as an inventory cache.
  FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
  FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
  window.GottaFacetController = FacetFiltersForm;
  window.FacetFiltersForm = FacetFiltersForm;
  if (!customElements.get('facet-filters-form')) customElements.define('facet-filters-form', FacetFiltersForm);

  class PriceRange extends HTMLElement {
    constructor() {
      super();
      this.querySelectorAll('input').forEach((input) => input.addEventListener('change', (event) => {
        this.adjustToValidValues(event.currentTarget);
        this.setMinAndMaxValues();
        // Validation may have clamped the value AFTER the original input event.
        event.currentTarget.dispatchEvent(new Event('input', { bubbles: true }));
      }));
      this.setMinAndMaxValues();
    }
    setMinAndMaxValues() {
      const [min, max] = this.querySelectorAll('input');
      if (!min || !max) return;
      min.setAttribute('max', max.value || max.getAttribute('max') || '');
      max.setAttribute('min', min.value || '0');
    }
    adjustToValidValues(input) {
      if (input.value === '') return;
      const value = Number(input.value);
      if (!Number.isFinite(value)) { input.value = ''; return; }
      const lower = input.getAttribute('min');
      const upper = input.getAttribute('max');
      if (lower !== null && lower !== '' && value < Number(lower)) input.value = lower;
      if (upper !== null && upper !== '' && value > Number(upper)) input.value = upper;
    }
  }
  if (!customElements.get('price-range')) customElements.define('price-range', PriceRange);

  class FacetRemove extends HTMLElement {
    constructor() {
      super();
      const link = this.querySelector('a');
      if (!link) return;
      link.setAttribute('role', 'button');
      link.addEventListener('click', (event) => {
        // Preserve open-in-new-tab semantics; the URL is already a native filter URL.
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        this.closeFilter(event);
      });
      link.addEventListener('keyup', (event) => { if (event.code === 'Space') this.closeFilter(event); });
    }
    closeFilter(event) {
      const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
      if (form) form.onActiveFilterClick(event);
    }
  }
  if (!customElements.get('facet-remove')) customElements.define('facet-remove', FacetRemove);

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[data-gotta-stock-page]');
    if (!link || !root()?.contains(link) || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    const next = new URL(link.href, window.location.href);
    FacetFiltersForm.renderPage(next.search, event).then((ok) => {
      if (ok) document.getElementById('product-grid')?.scrollIntoView({ block: 'start', behavior: 'auto' });
    });
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted && root()?.dataset.stockVersion === '3') {
      FacetFiltersForm.renderPage(window.location.search, null, false, { replaceURL: true });
    }
  });
  window.addEventListener('popstate', () => {
    if (!root()) return;
    const params = FacetFiltersForm.normalize(window.location.search);
    // Always use the address bar, not another integration's partial history.state.
    FacetFiltersForm.renderPage(params, null, false, { replaceURL: true });
  });
  document.addEventListener('shopify:section:load', () => FacetFiltersForm.bootstrap());
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.contains(root())) FacetFiltersForm.invalidate();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => FacetFiltersForm.bootstrap(), { once: true });
  else FacetFiltersForm.bootstrap();
})();
